import { useEffect, useState } from "react";
import { apiFetch } from "../../../api/client";
import { formatImportErrorMessage } from "../../../utils/importErrorMessage";
import TraderModal from "./TraderModal";

function getLanguageLabel(languageCode) {
  const labels = {
    en: "English",
    de: "German",
    fr: "French",
    es: "Spanish",
    it: "Italian",
    jp: "Japanese",
    cn: "Chinese",
    kr: "Korean",
    ru: "Russian",
    pt: "Portuguese"
  };

  return labels[String(languageCode || "").toLowerCase()] || String(languageCode || "en").toUpperCase();
}

export default function UploadTextModal({
  isOpen,
  onClose,
  userId,
  submitUrl,
  onUploaded,
  title = "Upload Text",
  defaultLanguage = "en",
  defaultCondition = "NM",
  defaultFoil = "false",
  defaultAltered = "false",
  defaultSigned = "false"
}) {
  const [text, setText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setText("");
      setIsSubmitting(false);
      setSubmitError("");
      setResult(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!text.trim()) {
      setSubmitError("Paste at least one card line to import.");
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");
      setResult(null);

      const response = await apiFetch(submitUrl, {
        userId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text })
      });
      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(formatImportErrorMessage(json, "Could not import text list"));
      }

      setResult(json);

      if (json.importedCount > 0 && onUploaded) {
        onUploaded(json);
      }

      if ((json.errors || []).length === 0) {
        onClose();
      }
    } catch (error) {
      setSubmitError(error.message || "Could not import text list");
    } finally {
      setIsSubmitting(false);
    }
  };

  const visibleErrors = Array.isArray(result?.errors) ? result.errors.slice(0, 8) : [];
  const hasMoreErrors = Array.isArray(result?.errors) && result.errors.length > visibleErrors.length;
  const formatBinaryLabel = (value) => (String(value || "false").toLowerCase() === "true" ? "Yes" : "No");

  return (
    <TraderModal isOpen={isOpen} onClose={onClose} title={title} width="760px">
      <div style={{ display: "grid", gap: "16px" }}>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.5 }}>
          Paste one card per line. Plain names import the first catalog printing, and cards are added using the user's current trader defaults.
        </p>
        <div
          style={{
            display: "grid",
            gap: "8px",
            padding: "14px 16px",
            borderRadius: "14px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#334155",
            fontSize: "13px",
            lineHeight: 1.5
          }}
        >
          <div>
            Default import: {getLanguageLabel(defaultLanguage)} / {String(defaultCondition || "NM").toUpperCase()} / Foil {formatBinaryLabel(defaultFoil)} / Altered {formatBinaryLabel(defaultAltered)} / Signed {formatBinaryLabel(defaultSigned)}
          </div>
          <div>Supported formats:</div>
          <div>Ponder</div>
          <div>4 Mind Grind</div>
          <div>Lightning Bolt (JMP)</div>
          <div>1 Abdel Adrian, Gorion&apos;s Ward (CLB) 2</div>
          <div>Elvish Mystic foil</div>
          <div>1 Mox Opal (2XM) 275 [F]</div>
        </div>
        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={"Ponder\nLightning Bolt (JMP)\nMox Opal foil\n1 Abdel Adrian, Gorion's Ward (CLB) 2\n1 Mox Opal (2XM) 275 [F]"}
          style={{
            width: "100%",
            minHeight: "320px",
            padding: "14px 16px",
            borderRadius: "14px",
            border: "1px solid #cbd5e1",
            boxSizing: "border-box",
            resize: "vertical",
            fontFamily: "inherit",
            fontSize: "14px"
          }}
        />
        {submitError && (
          <div style={{ padding: "12px 14px", borderRadius: "12px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c" }}>
            {submitError}
          </div>
        )}
        {result && (
          <div style={{ display: "grid", gap: "10px", padding: "14px 16px", borderRadius: "14px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>
              Imported {result.importedCount || 0} card{result.importedCount === 1 ? "" : "s"} from {result.totalLines || 0} line{result.totalLines === 1 ? "" : "s"}
            </div>
            {visibleErrors.length > 0 && (
              <div style={{ display: "grid", gap: "8px" }}>
                {visibleErrors.map((error) => (
                  <div key={`${error.lineNumber}-${error.line}`} style={{ fontSize: "13px", color: "#991b1b" }}>
                    Line {error.lineNumber}: {error.message} ({error.line})
                  </div>
                ))}
                {hasMoreErrors && (
                  <div style={{ fontSize: "13px", color: "#64748b" }}>
                    ...and {result.errors.length - visibleErrors.length} more error{result.errors.length - visibleErrors.length === 1 ? "" : "s"}.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            type="button"
            onClick={onClose}
            className="app-button-base"
            style={{ borderRadius: "10px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !text.trim()}
            className="app-button-base"
            style={{
              borderRadius: "10px",
              border: "none",
              background: isSubmitting || !text.trim() ? "#cbd5e1" : "#0f766e",
              color: isSubmitting || !text.trim() ? "#475569" : "#ffffff",
              cursor: isSubmitting || !text.trim() ? "not-allowed" : "pointer"
            }}
          >
            {isSubmitting ? "Importing..." : "Import text"}
          </button>
        </div>
      </div>
    </TraderModal>
  );
}